import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle,
  Download,
  Home,
  EmojiEvents,
  CalendarToday,
  CreditCard,
  Email,
  Message,
  Receipt,
  Verified,
} from '@mui/icons-material';
import { paymentAPI } from '../api/payment.api';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(41, 30, 108, 0.15)',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
}));

const SuccessCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(76, 175, 80, 0.05)',
  border: '2px solid #4caf50',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const InfoCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(41, 30, 108, 0.1)',
  },
}));

const StyledButton = styled(Button)({
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const Success = () => {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const paymentCompleted = localStorage.getItem('paymentCompleted');
    if (!paymentCompleted) {
      navigate('/');
      return;
    }
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    try {
      
      const athleteId = localStorage.getItem('athleteId');
      if (athleteId) {
        const response = await paymentAPI.getPaymentStatus(athleteId);
        if (response.success) {
          setPaymentData(response.data);
        }
      }
    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setDownloading(true);
    try {
      const athleteId = localStorage.getItem('athleteId');
const response = await paymentAPI.downloadInvoice(athleteId);
    
    if (response.data.url) {
      const { url, filename } = response.data;
      // Create invisible link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.setAttribute('target', '_blank');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Invoice downloaded successfully');
    }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return (amount / 100).toFixed(2);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} sx={{ color: '#291e6c' }} />
      </Box>
    );
  }

  if (!paymentData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          Payment data not found. Please contact support.
        </Alert>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate('/')}
          sx={{ mt: 2, backgroundColor: '#291e6c' }}
        >
          Go Home
        </Button>
      </Container>
    );
  }

  const verificationStatus = paymentData.verificationStatus || 'pending';
  const isApproved = verificationStatus === 'approved';

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        {/* Success Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                },
                '50%': {
                  transform: 'scale(1.05)',
                },
              },
            }}
          >
            <CheckCircle sx={{ fontSize: 60, color: '#4caf50' }} />
          </Box>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 800,
              color: '#291e6c',
              mb: 1,
            }}
          >
            PAYMENT SUCCESSFUL!
          </Typography>
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 400 }}>
            Your registration has been completed successfully
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Status Alert */}
            <SuccessCard>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  {isApproved ? (
                    <Verified sx={{ fontSize: 40, color: '#4caf50' }} />
                  ) : (
                    <CalendarToday sx={{ fontSize: 40, color: '#ff9800' }} />
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {isApproved ? 'Registration Approved' : 'Verification Pending'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {isApproved
                        ? 'Your senior registration has been automatically approved upon payment completion.'
                        : 'Your documents are pending verification by the admin. You will be notified once verified.'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </SuccessCard>

            {/* Payment Details */}
            <StyledPaper sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Receipt sx={{ color: '#291e6c', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                  Payment Details
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                        Invoice Number
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c' }}>
                        {paymentData.invoiceNumber}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                        Payment Status
                      </Typography>
                      <Chip
                        icon={<CheckCircle />}
                        label="Completed"
                        color="success"
                        sx={{ fontWeight: 600 }}
                      />
                    </CardContent>
                  </InfoCard>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                        Payment Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatDate(paymentData.paymentDate)}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InfoCard>
                    <CardContent>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                        Amount Paid
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        ₹{formatAmount(paymentData.amount)}
                      </Typography>
                    </CardContent>
                  </InfoCard>
                </Grid>

                {paymentData.paymentId && (
                  <Grid item xs={12}>
                    <InfoCard>
                      <CardContent>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                          Payment ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#555' }}>
                          {paymentData.paymentId}
                        </Typography>
                      </CardContent>
                    </InfoCard>
                  </Grid>
                )}
              </Grid>
            </StyledPaper>

            {/* Event Details */}
            {paymentData.selectedEvent && (
              <StyledPaper>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <EmojiEvents sx={{ color: '#291e6c', fontSize: 28 }} />
                  <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700 }}>
                    Event Details
                  </Typography>
                </Box>

                <Box sx={{ backgroundColor: 'rgba(41, 30, 108, 0.03)', p: 2, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c', mb: 1 }}>
                    {paymentData.selectedEvent.eventName}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Duration
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Lifetime
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </StyledPaper>
            )}
          </Grid>

          {/* Right Column - Actions */}
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Typography variant="h6" sx={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, mb: 3 }}>
                Next Steps
              </Typography>

              <Stack spacing={2}>
                <StyledButton
                  variant="contained"
                  fullWidth
                  startIcon={downloading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <Download />}
                  onClick={handleDownloadInvoice}
                  disabled={downloading}
                  sx={{ backgroundColor: '#291e6c', '&:hover': { backgroundColor: '#1f1750' } }}
                >
                  {downloading ? 'Downloading...' : 'Download Invoice'}
                </StyledButton>

                <StyledButton
                  variant="outlined"
                  fullWidth
                  startIcon={<Home />}
                  onClick={handleGoToDashboard}
                  sx={{
                    borderColor: '#291e6c',
                    color: '#291e6c',
                    '&:hover': {
                      borderColor: '#1f1750',
                      backgroundColor: 'rgba(41, 30, 108, 0.05)',
                    },
                  }}
                >
                  Go to Dashboard
                </StyledButton>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Alert severity="info" icon={<Email />} sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                  Confirmation Sent
                </Typography>
                <Typography variant="caption">
                  A confirmation email and SMS with your invoice has been sent to your registered contact details.
                </Typography>
              </Alert>

              {!isApproved && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    Verification in Progress
                  </Typography>
                  <Typography variant="caption">
                    Your documents are being verified. You'll receive an email once approved.
                  </Typography>
                </Alert>
              )}

              {isApproved && (
                <Alert severity="success" icon={<Verified />} sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    Admit Card Available
                  </Typography>
                  <Typography variant="caption">
                    Your admit card is being generated and will be available in your dashboard shortly.
                  </Typography>
                </Alert>
              )}
            </StyledPaper>
          </Grid>
        </Grid>
      </Container>
    </PageWrapper>
  );
};

export default Success;